import LockOutlined from '@ant-design/icons/LockOutlined'
import UnlockOutlined from '@ant-design/icons/UnlockOutlined'
import * as Toggle from '@radix-ui/react-toggle-group'
import { Switch } from 'antd'
import { TooltipWrapper } from 'components/TooltipWrapper'
import { useRolodexEdit } from 'features/fde'
import { useKestrel } from 'features/kestrelViewer'
import { useSortedAnnotations } from 'features/kestrelViewer/hooks/useSortAnnotations'
import { useAnnotationSelection } from 'features/kestrelViewer/kestrelViewerMarkup/annotations/useAnnotationSelection'
import { CameraViewEntry } from 'features/kestrelViewer/kestrelViewerMarkup/cameraViews/CameraViewEntry'
import { InspectionAreaEntry } from 'features/kestrelViewer/kestrelViewerMarkup/inspectionAreas/InspectionAreaEntry'
import { MeasurementEntry } from 'features/kestrelViewer/kestrelViewerMarkup/measurements/MeasurementEntry'
import { PlaneIntersectionEntry } from 'features/kestrelViewer/kestrelViewerMarkup/planeIntersections/PlaneIntersectionEntry'
import { PrimitiveEntry } from 'features/kestrelViewer/kestrelViewerMarkup/primitives/PrimitiveEntry'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { ConfirmationModal } from 'features/kestrelViewer/ui/popupMenu/shared/ConfirmationModal'
import { PermissionsGate } from 'features/permissions'
import { FC, useState } from 'react'
import styled from 'styled-components'
import {
  KestrelInspectionType,
  KVAnnotation,
  KVCameraView,
  KVInspectionArea,
  KVMeasurement,
  KVPlaneIntersection,
  KVPrimitive,
  PERMISSION_GECKO_INTERNAL_MEMBER,
  RolodexEntryPatchJson,
  RolodexEntryType,
} from 'types'

import { AerospaceMetadataSection } from './AerospaceMetadataSection'
import { AnnotationSection } from './annotations/AnnotationSection'
import { MarkupReorderTarget } from './MarkupReorderTarget'
import { ToggleAllVisibilityButton } from './ToggleAllVisibilityButton'
import { CollapseButton } from './utils/CollapseButton'

const EMPTY_ANNOTATIONS: KVAnnotation[] = []

const Section = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 12px;
`

const EntriesList = styled(Toggle.Root)`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const SectionTitleRow = styled.div<{ $isDragOver?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  position: sticky;
  top: -8px; /* Extend above the container to cover any gap */
  margin-top: -8px; /* Pull the element up to cover the gap */
  min-height: 2.5rem;
  background-color: ${({ theme, $isDragOver }) =>
    $isDragOver
      ? 'rgba(76, 175, 80, 0.2)'
      : theme.forecast.background2}; /* This matches the sidebar background */
  border: ${({ $isDragOver }) =>
    $isDragOver ? '2px solid rgba(76, 175, 80, 0.5)' : '2px solid transparent'};
  border-radius: 4px;
  transition: all 0.2s ease;
  z-index: 10;
`

const SectionTitleContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
`

export const SectionTitle = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.forecast.activeText};
  font-weight: bold;
  cursor: default;
  user-select: none;
`

const MovementToggleRow = styled.div<{ $isUnlocked: boolean }>`
  padding: 0.5rem 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
`

const MovementToggleContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const MovementLabel = styled.span<{ $isUnlocked: boolean }>`
  font-size: 0.875rem;
  color: ${({ $isUnlocked }) =>
    $isUnlocked ? 'rgba(255, 180, 0, 0.9)' : 'rgba(255, 255, 255, 0.7)'};
  transition: color 0.2s ease;
`

const MovementInstructions = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  padding: 0.25rem 0;
`

export const KestrelViewerMarkupSection: FC = () => {
  const {
    selectedMarkup,
    annotations,
    measurements,
    setMeasurements,
    planeIntersections,
    cameraViews,
    setCameraViews,
    kestrelModel,
    historicalAnnotations,
    inspectionAreas,
    setInspectionAreas,
    primitives,
    setPrimitives,
    annotationSortKey,
    sendKVCommand,
    inspectionType,
    gatekeeper,
  } = useKestrel()

  const { rolodexPatch } = useRolodexEdit()

  const [collapseInspectionAreas, setCollapseInspectionAreas] = useState(false)
  const [collapseCameraViews, setCollapseCameraViews] = useState(false)
  const [collapseMeasurements, setCollapseMeasurements] = useState(false)
  const [collapsePrimitives, setCollapsePrimitives] = useState(false)

  // Global primitive movement lock state
  const [isMovementAllowed, setIsMovementAllowed] = useState(false)

  const handleMovementToggle = (checked: boolean) => {
    setIsMovementAllowed(checked)

    if (sendKVCommand) {
      sendKVCommand({
        command: KestrelCommands.SET_PRIMITIVE_MOVEMENT_ALLOWED,
        allowed: checked,
      })
    }
  }

  const {
    showBatchDeleteConfirm,
    batchDeleteMessage,
    handleBatchDeleteConfirm,
    handleBatchDeleteCancel,
  } = useAnnotationSelection()

  // We use `EMPTY_ANNOTATIONS` instead of just `[]` so we have an object with
  // a stable id that can be used safely downstream in `useEffect` dependency
  // arrays without causing an infinite rerender loop.
  const { sortedAnnotations: groupedAnnotations } = useSortedAnnotations(
    annotations ?? EMPTY_ANNOTATIONS,
    annotationSortKey
  )

  const { sortedAnnotations: groupedHistoricalAnnotations } =
    useSortedAnnotations(
      historicalAnnotations ?? EMPTY_ANNOTATIONS,
      annotationSortKey
    )

  const handleCameraViewReorder = (
    draggedId: string,
    targetId: string,
    position: 'before' | 'after'
  ) => {
    if (!cameraViews || !kestrelModel?.id) {
      return
    }

    const draggedIndex = cameraViews.findIndex((cv) => cv.id === draggedId)
    const targetIndex = cameraViews.findIndex((cv) => cv.id === targetId)

    if (draggedIndex === -1 || targetIndex === -1) {
      return
    }

    // Remove the dragged item
    const newCameraViews = [...cameraViews]
    const [draggedItem] = newCameraViews.splice(draggedIndex, 1)

    // Calculate the insert position
    let insertIndex = targetIndex
    if (draggedIndex < targetIndex) {
      // If dragging down, adjust for the removed item
      insertIndex = position === 'before' ? targetIndex - 1 : targetIndex
    } else {
      // If dragging up
      insertIndex = position === 'before' ? targetIndex : targetIndex + 1
    }

    // Insert at the correct position
    newCameraViews.splice(insertIndex, 0, draggedItem)

    // Update state
    setCameraViews(newCameraViews)

    // Persist to Rolodex
    const patchData: RolodexEntryPatchJson = {
      id: kestrelModel.id,
      type: RolodexEntryType.KESTREL_MODEL,
      data: {
        cameraViews: newCameraViews.map((view) => KVCameraView.toJSON(view)),
      },
    }

    rolodexPatch([patchData]).catch((error) => {
      console.error('Rolodex patch failed:', error)
    })
  }

  const handleInspectionAreaReorder = (
    draggedId: string,
    targetId: string,
    position: 'before' | 'after'
  ) => {
    if (!inspectionAreas || !kestrelModel?.id) {
      return
    }

    const draggedIndex = inspectionAreas.findIndex((ia) => ia.id === draggedId)
    const targetIndex = inspectionAreas.findIndex((ia) => ia.id === targetId)

    if (draggedIndex === -1 || targetIndex === -1) {
      return
    }

    // Remove the dragged item
    const newInspectionAreas = [...inspectionAreas]
    const [draggedItem] = newInspectionAreas.splice(draggedIndex, 1)

    // Calculate the insert position
    let insertIndex = targetIndex
    if (draggedIndex < targetIndex) {
      // If dragging down, adjust for the removed item
      insertIndex = position === 'before' ? targetIndex - 1 : targetIndex
    } else {
      // If dragging up
      insertIndex = position === 'before' ? targetIndex : targetIndex + 1
    }

    // Insert at the correct position
    newInspectionAreas.splice(insertIndex, 0, draggedItem)

    // Update state
    setInspectionAreas(newInspectionAreas)

    // Persist to Rolodex
    const patchData: RolodexEntryPatchJson = {
      id: kestrelModel.id,
      type: RolodexEntryType.KESTREL_MODEL,
      data: {
        inspectionAreas: newInspectionAreas.map((ia) =>
          KVInspectionArea.toJSON(ia)
        ),
      },
    }

    rolodexPatch([patchData]).catch((error) => {
      console.error('Rolodex patch failed:', error)
    })
  }

  const handleMeasurementReorder = (
    draggedId: string,
    targetId: string,
    position: 'before' | 'after'
  ) => {
    if (!measurements || !kestrelModel?.id) {
      return
    }

    const draggedIndex = measurements.findIndex((m) => m.id === draggedId)
    const targetIndex = measurements.findIndex((m) => m.id === targetId)

    if (draggedIndex === -1 || targetIndex === -1) {
      return
    }

    // Remove the dragged item
    const newMeasurements = [...measurements]
    const [draggedItem] = newMeasurements.splice(draggedIndex, 1)

    // Calculate the insert position
    let insertIndex = targetIndex
    if (draggedIndex < targetIndex) {
      // If dragging down, adjust for the removed item
      insertIndex = position === 'before' ? targetIndex - 1 : targetIndex
    } else {
      // If dragging up
      insertIndex = position === 'before' ? targetIndex : targetIndex + 1
    }

    // Insert at the correct position
    newMeasurements.splice(insertIndex, 0, draggedItem)

    // Update state
    setMeasurements(newMeasurements)

    // Persist to Rolodex
    const patchData: RolodexEntryPatchJson = {
      id: kestrelModel.id,
      type: RolodexEntryType.KESTREL_MODEL,
      data: {
        measurements: newMeasurements.map((m) => KVMeasurement.toJSON(m)),
      },
    }

    rolodexPatch([patchData]).catch((error) => {
      console.error('Rolodex patch failed:', error)
    })
  }

  const handlePrimitiveReorder = (
    draggedId: string,
    targetId: string,
    position: 'before' | 'after'
  ) => {
    if (!primitives || !kestrelModel?.id) {
      return
    }

    const draggedIndex = primitives.findIndex((p) => p.id === draggedId)
    const targetIndex = primitives.findIndex((p) => p.id === targetId)

    if (draggedIndex === -1 || targetIndex === -1) {
      return
    }

    // Remove the dragged item
    const newPrimitives = [...primitives]
    const [draggedItem] = newPrimitives.splice(draggedIndex, 1)

    // Calculate the insert position
    let insertIndex = targetIndex
    if (draggedIndex < targetIndex) {
      // If dragging down, adjust for the removed item
      insertIndex = position === 'before' ? targetIndex - 1 : targetIndex
    } else {
      // If dragging up
      insertIndex = position === 'before' ? targetIndex : targetIndex + 1
    }

    // Insert at the correct position
    newPrimitives.splice(insertIndex, 0, draggedItem)

    // Update state
    setPrimitives(newPrimitives)

    // Persist to Rolodex
    const patchData: RolodexEntryPatchJson = {
      id: kestrelModel.id,
      type: RolodexEntryType.KESTREL_MODEL,
      data: {
        primitives: newPrimitives.map((p) => KVPrimitive.toJSON(p)),
      },
    }

    rolodexPatch([patchData]).catch((error) => {
      console.error('Rolodex patch failed:', error)
    })
  }

  return (
    <>
      {gatekeeper.feature.aircraftMetadata && (
        <>
          <AerospaceMetadataSection />
          <hr />
        </>
      )}

      {!!cameraViews && (
        <Section>
          <SectionTitleRow>
            <TooltipWrapper
              label='Click a Camera View below to navigate to that view. Drag to reorder. Save your current Camera View by clicking the flag icon.'
              side='right'
              offset={5}>
              <SectionTitle>Camera Views</SectionTitle>
            </TooltipWrapper>
            <CollapseButton
              handleCollapse={(collapsed: boolean) =>
                setCollapseCameraViews(collapsed)
              }
            />
          </SectionTitleRow>
          {!collapseCameraViews && (
            <EntriesList
              type='single'
              aria-label='Select camera view'
              onClick={(event) => {
                event.stopPropagation()
              }}>
              {cameraViews.map((cv: KVCameraView) => (
                <CameraViewEntry
                  key={cv.id}
                  cameraView={cv}
                  selectedMarkupId={selectedMarkup?.id}
                  onReorder={handleCameraViewReorder}
                />
              ))}
              {cameraViews.length > 0 && (
                <MarkupReorderTarget
                  dataKey='cameraViewId'
                  onDrop={(data) => {
                    const lastCameraView = cameraViews[cameraViews.length - 1]
                    handleCameraViewReorder(data, lastCameraView.id, 'after')
                  }}
                />
              )}
            </EntriesList>
          )}
        </Section>
      )}

      {!!inspectionAreas && (
        <Section>
          <SectionTitleRow>
            <SectionTitleContent>
              <TooltipWrapper
                label='Click an Inspection Area below to edit, or hover to hide or delete. Drag to reorder. Create a new Inspection Area by clicking the pentagon icon.'
                side='right'
                offset={5}>
                <SectionTitle>Inspection Areas</SectionTitle>
              </TooltipWrapper>
            </SectionTitleContent>
            <ToggleAllVisibilityButton
              type='inspectionAreas'
              count={inspectionAreas.length}
            />
            <CollapseButton
              handleCollapse={(collapsed: boolean) =>
                setCollapseInspectionAreas(collapsed)
              }
            />
          </SectionTitleRow>
          {!collapseInspectionAreas && (
            <EntriesList
              type='single'
              aria-label='Select inspection area'
              onClick={(event) => {
                event.stopPropagation()
              }}>
              {inspectionAreas.map((ia: KVInspectionArea) => (
                <InspectionAreaEntry
                  key={ia.id}
                  inspectionArea={ia}
                  selectedMarkupId={selectedMarkup?.id}
                  onReorder={handleInspectionAreaReorder}
                />
              ))}
              {inspectionAreas.length > 0 && (
                <MarkupReorderTarget
                  dataKey='inspectionAreaId'
                  onDrop={(data) => {
                    const lastInspectionArea =
                      inspectionAreas[inspectionAreas.length - 1]
                    handleInspectionAreaReorder(
                      data,
                      lastInspectionArea.id,
                      'after'
                    )
                  }}
                />
              )}
            </EntriesList>
          )}
        </Section>
      )}

      {!!annotations && (
        <AnnotationSection
          sectionTitle='Annotations'
          sectionAnnotations={groupedAnnotations}
          batchActions
        />
      )}

      {inspectionType === KestrelInspectionType.ROOFTOP &&
        !!historicalAnnotations &&
        historicalAnnotations.length > 0 && (
          <AnnotationSection
            sectionTitle='Historical Annotations'
            sectionAnnotations={groupedHistoricalAnnotations}
            historical
          />
        )}

      {!!measurements && (
        <Section>
          <SectionTitleRow>
            <SectionTitleContent>
              <TooltipWrapper
                label='Click a Measurement below to edit, or hover to hide or delete. Drag to reorder. Create a new Measurement by clicking the ruler icon.'
                side='right'
                offset={5}>
                <SectionTitle>Measurements</SectionTitle>
              </TooltipWrapper>
            </SectionTitleContent>
            <ToggleAllVisibilityButton
              type='measurements'
              count={measurements.length}
            />
            <CollapseButton
              handleCollapse={(collapsed: boolean) =>
                setCollapseMeasurements(collapsed)
              }
            />
          </SectionTitleRow>
          {!collapseMeasurements && (
            <EntriesList
              type='single'
              aria-label='Select measurement'
              onClick={(event) => {
                event.stopPropagation()
              }}>
              {measurements.map((m: KVMeasurement) => (
                <MeasurementEntry
                  key={m.id}
                  measurement={m}
                  selectedMarkupId={selectedMarkup?.id}
                  onReorder={handleMeasurementReorder}
                />
              ))}
              {measurements.length > 0 && (
                <MarkupReorderTarget
                  dataKey='measurementId'
                  onDrop={(data) => {
                    const lastMeasurement =
                      measurements[measurements.length - 1]
                    handleMeasurementReorder(data, lastMeasurement.id, 'after')
                  }}
                />
              )}
            </EntriesList>
          )}
        </Section>
      )}

      {!!primitives && (
        <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
          <Section>
            <SectionTitleRow>
              <SectionTitleContent>
                <TooltipWrapper
                  label='Click a Primitive below to edit, or hover to hide or delete. Drag to reorder.'
                  side='right'
                  offset={5}>
                  <SectionTitle>Primitives</SectionTitle>
                </TooltipWrapper>
              </SectionTitleContent>
              <ToggleAllVisibilityButton
                type='primitives'
                count={primitives.length}
              />
              <CollapseButton
                handleCollapse={(collapsed: boolean) =>
                  setCollapsePrimitives(collapsed)
                }
              />
            </SectionTitleRow>
            {primitives.length > 0 && (
              <MovementToggleRow $isUnlocked={isMovementAllowed}>
                <MovementToggleContent>
                  <MovementLabel $isUnlocked={isMovementAllowed}>
                    Move Primitives: {isMovementAllowed ? 'Unlocked' : 'Locked'}
                  </MovementLabel>
                  <Switch
                    size='small'
                    checked={isMovementAllowed}
                    onChange={handleMovementToggle}
                    checkedChildren={<UnlockOutlined />}
                    unCheckedChildren={<LockOutlined />}
                  />
                </MovementToggleContent>
                {isMovementAllowed && (
                  <MovementInstructions>
                    <span>• Drag primitive to move on X-Y plane</span>
                    <span>• Shift + Drag to move on Z axis</span>
                  </MovementInstructions>
                )}
              </MovementToggleRow>
            )}
            {!collapsePrimitives && (
              <EntriesList
                type='single'
                aria-label='Select primitive'
                onClick={(event) => {
                  event.stopPropagation()
                }}>
                {primitives.map((p: KVPrimitive) => (
                  <PrimitiveEntry
                    key={p.id}
                    primitive={p}
                    selectedMarkupId={selectedMarkup?.id}
                    onReorder={handlePrimitiveReorder}
                  />
                ))}
                {primitives.length > 0 && (
                  <MarkupReorderTarget
                    dataKey='primitiveId'
                    onDrop={(data) => {
                      const lastPrimitive = primitives[primitives.length - 1]
                      handlePrimitiveReorder(data, lastPrimitive.id, 'after')
                    }}
                  />
                )}
              </EntriesList>
            )}
          </Section>
        </PermissionsGate>
      )}

      {!!planeIntersections &&
        inspectionType !== KestrelInspectionType.NAVY &&
        inspectionType !== KestrelInspectionType.ROOFTOP && (
          <Section>
            <SectionTitleRow>
              <TooltipWrapper
                label='Click a Plane Intersection below to edit, or hover to delete. Create a new Plane Intersection by clicking the circle-in-a-box icon.'
                side='right'
                offset={5}>
                <SectionTitle>Plane Intersections</SectionTitle>
              </TooltipWrapper>
            </SectionTitleRow>
            <EntriesList
              type='single'
              aria-label='Select plane intersection'
              onClick={(event) => {
                event.stopPropagation()
              }}>
              {planeIntersections.map((p: KVPlaneIntersection) => (
                <PlaneIntersectionEntry
                  key={p.id}
                  planeIntersection={p}
                  selectedMarkupId={selectedMarkup?.id}
                />
              ))}
            </EntriesList>
          </Section>
        )}

      <ConfirmationModal
        isOpen={showBatchDeleteConfirm}
        title='Delete Annotations'
        message={batchDeleteMessage}
        confirmText='Delete'
        cancelText='Cancel'
        onConfirm={handleBatchDeleteConfirm}
        onCancel={handleBatchDeleteCancel}
      />
    </>
  )
}
